import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";
import useWorkspaceCacheUpdate from "common/datahooks/useWorkspaceCacheUpdate";
import { del, patch, post } from "common/helpers/HTTP";
import type {
  Member,
  UserPermissions,
  UserRole,
  Workspace,
} from "common/types";

async function inviteMemberRequest(
  subscriptionId: string,
  email: string,
  permissions: UserPermissions,
  role: UserRole,
): Promise<Member> {
  return (
    await post(`workspaces/${subscriptionId}/members`, {
      email: email.toLocaleLowerCase(),
      permissions,
      role,
    })
  ).invite;
}

async function changeMemberRoleRequest(
  workspaceId: string,
  memberId: string,
  permissions: UserPermissions,
  role: UserRole,
) {
  return patch(`workspaces/${workspaceId}/members/${memberId}`, {
    permissions,
    role,
  });
}

async function removeMemberRequest(workspaceId: string, memberId: string) {
  await del(`workspaces/${workspaceId}/members/${memberId}`);
}

async function revokeInviteRequest(workspaceId: string, inviteToken: string) {
  return del(`workspaces/${workspaceId}/invites/${inviteToken}`);
}

async function resendInviteRequest(workspaceId: string, inviteToken: string) {
  return post(`workspaces/${workspaceId}/invites/${inviteToken}`);
}

export default function useMembersMutations() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id: workspaceId } = useSelectedWorkspaceContext();
  const { membersKeys } = getQueryKeys(workspaceId);
  const { updateCachedWorkspaceDetails, updateCachedWorkspaceList } =
    useWorkspaceCacheUpdate();

  const {
    mutateAsync: inviteMember,
    isPending: isInvitingMember,
    error: inviteMemberError,
    reset: resetInviteMember,
  } = useMutation({
    mutationFn: ({
      email,
      permissions,
      role,
    }: {
      email: string;
      permissions: UserPermissions;
      role: UserRole;
    }) => inviteMemberRequest(workspaceId, email, permissions, role),
    onSuccess: (member) => {
      queryClient.setQueryData(membersKeys.list, (prevMembers: Member[]) => [
        ...prevMembers,
        member,
      ]);
      updateCachedWorkspaceDetails(workspaceId, (draftWorkspace) => {
        draftWorkspace.member_count += 1;
      });
      updateCachedWorkspaceList(workspaceId, (draftWorkspace) => {
        draftWorkspace.member_count += 1;
      });

      toast.success("Invite sent");
    },
  });

  const { mutateAsync: changeMemberRole, isPending: isChangingRole } =
    useMutation({
      mutationFn: ({
        memberId,
        permissions,
        role,
      }: {
        memberId: string;
        permissions: UserPermissions;
        role: UserRole;
      }) => changeMemberRoleRequest(workspaceId, memberId, permissions, role),
      onSuccess: (_, { memberId, permissions, role }) => {
        queryClient.setQueryData<Member[]>(membersKeys.list, (prevMembers) => {
          if (prevMembers) {
            return prevMembers.map((member) =>
              member.customer_id === memberId
                ? { ...member, permissions, role }
                : member,
            );
          }
        });
        toast.success("Role updated");
      },
    });

  const { mutateAsync: removeMember, isPending: isRemovingMember } =
    useMutation({
      mutationFn: ({ memberId }: { memberId: string }) =>
        removeMemberRequest(workspaceId, memberId),
      onSuccess: (_, { memberId }) => {
        queryClient.setQueryData(membersKeys.list, (prevMembers: Member[]) =>
          prevMembers.filter(({ customer_id }) => customer_id !== memberId),
        );
        updateCachedWorkspaceDetails(workspaceId, (draftWorkspace) => {
          draftWorkspace.member_count -= 1;
        });
        updateCachedWorkspaceList(workspaceId, (draftWorkspace) => {
          draftWorkspace.member_count -= 1;
        });

        toast.success("Member removed");
      },
    });

  const { mutateAsync: leaveWorkspace, isPending: isLeavingWorkspace } =
    useMutation({
      mutationFn: ({ memberId }: { memberId: string }) =>
        removeMemberRequest(workspaceId, memberId),
      onSuccess: (_, { memberId }) => {
        queryClient.setQueryData(membersKeys.list, (prevMembers: Member[]) =>
          prevMembers.filter(({ customer_id }) => customer_id !== memberId),
        );
        navigate("/workspaces");
        queryClient.setQueryData<Workspace[]>(
          ["workspaces"],
          (prevWorkspaces) =>
            prevWorkspaces.filter((workspace) => workspace.id !== workspaceId),
        );
        toast.success("You've left workspace");
      },
    });

  const { mutateAsync: revokeInvite, isPending: isRevokingInvite } =
    useMutation({
      mutationFn: ({ inviteToken }: { inviteToken: string }) =>
        revokeInviteRequest(workspaceId, inviteToken),
      onSuccess: (_, { inviteToken }) => {
        queryClient.setQueryData(
          membersKeys.list,
          (prevMembers: Member[] | undefined) =>
            prevMembers?.filter((member) => member.token !== inviteToken) ?? [],
        );
        updateCachedWorkspaceDetails(workspaceId, (draftWorkspace) => {
          draftWorkspace.member_count -= 1;
        });
        updateCachedWorkspaceList(workspaceId, (draftWorkspace) => {
          draftWorkspace.member_count -= 1;
        });

        toast.success("Invite revoked");
      },
    });

  const { mutateAsync: resendInvite, isPending: isResendingInvite } =
    useMutation({
      mutationFn: ({ inviteToken }: { inviteToken: string }) =>
        resendInviteRequest(workspaceId, inviteToken),
      onSuccess: () => {
        toast.success("Invite resent");
      },
    });

  return {
    inviteMember,
    isInvitingMember,
    inviteMemberError,
    resetInviteMember,
    removeMember,
    isRemovingMember,
    leaveWorkspace,
    isLeavingWorkspace,
    changeMemberRole,
    isChangingRole,
    revokeInvite,
    isRevokingInvite,
    resendInvite,
    isResendingInvite,
  };
}
